/**
 * Feature-local async scope coordination for companyWorkspace.
 * Separates:
 * - lifetime (identity/route/unmount invalidation)
 * - read freshness (stale load discard)
 * - mutation ownership (busy release; own reload must not freeze busy)
 *
 * D2 R1: optional membershipGeneration + resourceId binding for
 * company Property create/edit chains. Backward compatible — callers that
 * omit the new fields keep prior actor/company lifetime behavior.
 *
 * Not a shared hook library — no Redux/auth/WS changes.
 */

export function sameCompanyId(a, b) {
  if (a == null || b == null) return false;
  return String(a) === String(b);
}

export function sameActorId(a, b) {
  if (a == null || b == null) return false;
  return String(a) === String(b);
}

export function sameMembershipGeneration(a, b) {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return String(a) === String(b);
}

function normalizeId(value) {
  return value == null ? null : String(value);
}

function normalizeGeneration(value) {
  return value == null ? null : value;
}

export function createScopeGate() {
  let lifetime = 0;
  let readGeneration = 0;
  let boundCompanyId = null;
  let boundActorId = null;
  let boundMembershipGeneration = null;
  let membershipGenerationBound = false;
  let boundResourceId = null;
  let resourceIdBound = false;

  const bumpLifetime = () => {
    lifetime += 1;
    readGeneration += 1;
    return lifetime;
  };

  return {
    /**
     * Bind current route/actor (and optional membership generation / resource).
     * Bumps lifetime when any bound identity field changes so captured operation
     * snapshots cannot validate against stale closures.
     */
    bindIdentity({
      companyId = null,
      actorId = null,
      membershipGeneration,
      resourceId,
      bindMembershipGeneration = false,
      bindResourceId = false,
    } = {}) {
      const nextCompany = normalizeId(companyId);
      const nextActor = normalizeId(actorId);
      const shouldBindGeneration =
        bindMembershipGeneration ||
        Object.prototype.hasOwnProperty.call(
          arguments[0] || {},
          "membershipGeneration",
        );
      const shouldBindResource =
        bindResourceId ||
        Object.prototype.hasOwnProperty.call(arguments[0] || {}, "resourceId");

      const nextGeneration = shouldBindGeneration
        ? normalizeGeneration(membershipGeneration)
        : boundMembershipGeneration;
      const nextResource = shouldBindResource
        ? normalizeId(resourceId)
        : boundResourceId;

      const generationChanged =
        shouldBindGeneration &&
        (!membershipGenerationBound ||
          !sameMembershipGeneration(nextGeneration, boundMembershipGeneration));
      const resourceChanged =
        shouldBindResource &&
        (!resourceIdBound || nextResource !== boundResourceId);

      if (
        nextCompany !== boundCompanyId ||
        nextActor !== boundActorId ||
        generationChanged ||
        resourceChanged
      ) {
        bumpLifetime();
        boundCompanyId = nextCompany;
        boundActorId = nextActor;
        if (shouldBindGeneration) {
          boundMembershipGeneration = nextGeneration;
          membershipGenerationBound = true;
        }
        if (shouldBindResource) {
          boundResourceId = nextResource;
          resourceIdBound = true;
        }
      } else {
        boundCompanyId = nextCompany;
        boundActorId = nextActor;
        if (shouldBindGeneration) {
          boundMembershipGeneration = nextGeneration;
          membershipGenerationBound = true;
        }
        if (shouldBindResource) {
          boundResourceId = nextResource;
          resourceIdBound = true;
        }
      }
      return lifetime;
    },

    /** Hard invalidate (unmount, focus, or explicit cancel). */
    invalidate() {
      return bumpLifetime();
    },

    /** @deprecated Prefer invalidate() / bindIdentity(). */
    bump() {
      return this.invalidate();
    },

    currentLifetime() {
      return lifetime;
    },

    getBoundIdentity() {
      return {
        companyId: boundCompanyId,
        actorId: boundActorId,
        membershipGeneration: membershipGenerationBound
          ? boundMembershipGeneration
          : null,
        membershipGenerationBound,
        resourceId: resourceIdBound ? boundResourceId : null,
        resourceIdBound,
        lifetime,
      };
    },

    /**
     * Start a read. Bumps read generation only — must not freeze an in-flight
     * mutation's busy ownership.
     */
    beginRead({
      companyId = null,
      actorId = null,
      resourceId = null,
      membershipGeneration,
    } = {}) {
      readGeneration += 1;
      const hasGeneration = Object.prototype.hasOwnProperty.call(
        arguments[0] || {},
        "membershipGeneration",
      );
      return {
        kind: "read",
        lifetime,
        readGeneration,
        companyId: normalizeId(companyId),
        actorId: normalizeId(actorId),
        resourceId: normalizeId(resourceId),
        membershipGeneration: hasGeneration
          ? normalizeGeneration(membershipGeneration)
          : membershipGenerationBound
            ? boundMembershipGeneration
            : undefined,
      };
    },

    /**
     * Start a mutation. Captures lifetime without bumping read generation so
     * a same-scope canonical reload after success can still release busy.
     */
    beginMutation({
      companyId = null,
      actorId = null,
      resourceId = null,
      membershipGeneration,
    } = {}) {
      const hasGeneration = Object.prototype.hasOwnProperty.call(
        arguments[0] || {},
        "membershipGeneration",
      );
      return {
        kind: "mutation",
        lifetime,
        companyId: normalizeId(companyId),
        actorId: normalizeId(actorId),
        resourceId: normalizeId(resourceId),
        membershipGeneration: hasGeneration
          ? normalizeGeneration(membershipGeneration)
          : membershipGenerationBound
            ? boundMembershipGeneration
            : undefined,
      };
    },

    readStillValid(op, { mounted = true } = {}) {
      if (!mounted || !op || op.kind !== "read") return false;
      if (op.lifetime !== lifetime) return false;
      if (op.readGeneration !== readGeneration) return false;
      if (
        boundCompanyId != null &&
        op.companyId != null &&
        op.companyId !== boundCompanyId
      ) {
        return false;
      }
      if (
        boundActorId != null &&
        op.actorId != null &&
        op.actorId !== boundActorId
      ) {
        return false;
      }
      if (
        membershipGenerationBound &&
        op.membershipGeneration !== undefined &&
        !sameMembershipGeneration(
          op.membershipGeneration,
          boundMembershipGeneration,
        )
      ) {
        return false;
      }
      if (
        resourceIdBound &&
        boundResourceId != null &&
        op.resourceId != null &&
        op.resourceId !== boundResourceId
      ) {
        return false;
      }
      return true;
    },

    mutationStillValid(op, { mounted = true } = {}) {
      if (!mounted || !op || op.kind !== "mutation") return false;
      if (op.lifetime !== lifetime) return false;
      if (
        boundCompanyId != null &&
        op.companyId != null &&
        op.companyId !== boundCompanyId
      ) {
        return false;
      }
      if (
        boundActorId != null &&
        op.actorId != null &&
        op.actorId !== boundActorId
      ) {
        return false;
      }
      if (
        membershipGenerationBound &&
        op.membershipGeneration !== undefined &&
        !sameMembershipGeneration(
          op.membershipGeneration,
          boundMembershipGeneration,
        )
      ) {
        return false;
      }
      if (
        resourceIdBound &&
        boundResourceId != null &&
        op.resourceId != null &&
        op.resourceId !== boundResourceId
      ) {
        return false;
      }
      return true;
    },

    /**
     * Post-reload / clipboard continuation against LIVE lifetime — never
     * compare captured closure ids to themselves.
     */
    lifetimeStillValid(op, { mounted = true } = {}) {
      if (!mounted || !op) return false;
      return op.lifetime === lifetime;
    },
  };
}

/** @deprecated Prefer gate.beginMutation / gate.beginRead. */
export function beginOperation(gate, opts) {
  return gate.beginMutation(opts);
}

/** Dispatch to read/mutation validators based on operation kind. */
export function operationStillValid(gate, operation, opts = {}) {
  if (!operation) return false;
  if (operation.kind === "read") return gate.readStillValid(operation, opts);
  return gate.mutationStillValid(operation, opts);
}

/**
 * Live lifetime check for post-reload token/clipboard continuation.
 * Signature: (gate, operation, { mounted }) — gate is required.
 */
export function identityStillValid(gate, operation, opts = {}) {
  return gate.lifetimeStillValid(operation, opts);
}
